import { useState } from 'react';
import { Trans, useLingui } from '@lingui/react/macro';
import { createFileRoute, Link, useNavigate } from '@tanstack/react-router';
import { z } from 'zod';
import { Button, Card, CardContent } from '@pantry/ui';
import { cn } from '@pantry/ui';
import { useInvitationPreview, useAcceptInvitation } from '@pantry/api-client';
import { useAuth } from '../../contexts/useAuth';
import {
  UserGroupIcon,
  CheckCircleIcon,
  ExclamationTriangleIcon,
  ClockIcon,
  HomeIcon,
  EnvelopeIcon,
  ShieldCheckIcon,
} from '@heroicons/react/24/outline';

const searchSchema = z.object({
  token: z.string().optional(),
});

export const Route = createFileRoute('/_auth/invitations/accept')({
  validateSearch: searchSchema,
  component: AcceptInvitationPage,
});

function AcceptInvitationPage() {
  const { t, i18n } = useLingui();
  const { token } = Route.useSearch();
  const navigate = useNavigate();
  const { user, session } = useAuth();

  const [acceptError, setAcceptError] = useState<string | null>(null);
  const [acceptSuccess, setAcceptSuccess] = useState(false);

  const getRoleLabel = (role: string): string => {
    switch (role) {
      case 'owner':
        return t`Owner`;
      case 'admin':
        return t`Admin`;
      case 'member':
        return t`Member`;
      default:
        return role;
    }
  };

  const {
    data: preview,
    isLoading,
    error: previewError,
  } = useInvitationPreview(token || '', {
    enabled: !!token,
  });

  const acceptInvitation = useAcceptInvitation();

  const handleAccept = async () => {
    if (!token) return;
    setAcceptError(null);

    try {
      const result = await acceptInvitation.mutateAsync({ token });
      setAcceptSuccess(true);
      // Navigate to the household after a brief delay
      setTimeout(() => {
        navigate({ to: '/households/$householdId', params: { householdId: result.householdId } });
      }, 1500);
    } catch (err) {
      const message = err instanceof Error ? err.message : t`Failed to accept invitation`;
      setAcceptError(message);
    }
  };

  // No token provided
  if (!token) {
    return (
      <Card className="w-full max-w-md rounded-2xl shadow-lg animate-in fade-in slide-in-from-bottom-4 duration-500">
        <CardContent className="pt-8 pb-8 text-center">
          <div className="mx-auto w-16 h-16 rounded-2xl bg-destructive/10 flex items-center justify-center mb-6">
            <ExclamationTriangleIcon className="h-8 w-8 text-destructive" />
          </div>
          <h2 className="font-display text-2xl font-semibold text-foreground mb-3">
            <Trans>Invalid Invitation</Trans>
          </h2>
          <p className="text-muted-foreground mb-8 max-w-xs mx-auto">
            <Trans>
              No invitation token was provided. Please check your invitation link and try again.
            </Trans>
          </p>
          <Button asChild size="lg" className="rounded-xl">
            <Link to="/">
              <HomeIcon className="h-4 w-4 mr-2" />
              <Trans>Go Home</Trans>
            </Link>
          </Button>
        </CardContent>
      </Card>
    );
  }

  // Loading state
  if (isLoading) {
    return (
      <Card className="w-full max-w-md rounded-2xl shadow-lg animate-in fade-in duration-300">
        <CardContent className="pt-12 pb-12 text-center">
          <div className="mx-auto w-16 h-16 rounded-2xl bg-primary/10 flex items-center justify-center mb-6">
            <div className="h-8 w-8 animate-spin rounded-full border-3 border-primary border-t-transparent" />
          </div>
          <p className="text-muted-foreground">
            <Trans>Loading invitation details...</Trans>
          </p>
        </CardContent>
      </Card>
    );
  }

  // Error fetching preview
  if (previewError || !preview) {
    const errorMessage = previewError?.message || 'Invitation not found';
    const isExpired = errorMessage.toLowerCase().includes('expired');
    const isUsed = errorMessage.toLowerCase().includes('already');

    return (
      <Card className="w-full max-w-md rounded-2xl shadow-lg animate-in fade-in slide-in-from-bottom-4 duration-500">
        <CardContent className="pt-8 pb-8 text-center">
          <div
            className={cn(
              'mx-auto w-16 h-16 rounded-2xl flex items-center justify-center mb-6',
              isExpired ? 'bg-expiring-soon/10' : 'bg-destructive/10'
            )}
          >
            {isExpired ? (
              <ClockIcon className="h-8 w-8 text-expiring-soon" />
            ) : (
              <ExclamationTriangleIcon className="h-8 w-8 text-destructive" />
            )}
          </div>
          <h2 className="font-display text-2xl font-semibold text-foreground mb-3">
            {isExpired ? (
              <Trans>Invitation Expired</Trans>
            ) : isUsed ? (
              <Trans>Already Accepted</Trans>
            ) : (
              <Trans>Invalid Invitation</Trans>
            )}
          </h2>
          <p className="text-muted-foreground mb-8 max-w-xs mx-auto">
            {isExpired
              ? t`This invitation has expired. Please ask the household owner to send a new one.`
              : isUsed
                ? t`This invitation has already been accepted.`
                : errorMessage}
          </p>
          <Button asChild size="lg" variant="outline" className="rounded-xl">
            <Link to="/">
              <HomeIcon className="h-4 w-4 mr-2" />
              <Trans>Go Home</Trans>
            </Link>
          </Button>
        </CardContent>
      </Card>
    );
  }

  // Success state
  if (acceptSuccess) {
    return (
      <Card className="w-full max-w-md rounded-2xl shadow-lg animate-in fade-in zoom-in-95 duration-500">
        <CardContent className="pt-8 pb-8 text-center">
          <div className="mx-auto w-20 h-20 rounded-full bg-fresh/10 flex items-center justify-center mb-6 animate-in zoom-in duration-300">
            <CheckCircleIcon className="h-10 w-10 text-fresh" />
          </div>
          <h2 className="font-display text-2xl font-semibold text-foreground mb-3">
            <Trans>Welcome to {preview.household.name}!</Trans>
          </h2>
          <p className="text-muted-foreground">
            <Trans>You&apos;ve successfully joined the household.</Trans>
          </p>
          <p className="text-sm text-muted-foreground mt-2">
            <Trans>Redirecting...</Trans>
          </p>
        </CardContent>
      </Card>
    );
  }

  // Show invitation preview - compute expiry values
  // Note: These are computed here (not memoized) since hooks cannot be called conditionally
  const expiryDate = new Date(preview.invitation.expiresAt);
  const now = new Date();
  const isExpired = expiryDate <= now;
  const daysUntilExpiry = Math.ceil((expiryDate.getTime() - now.getTime()) / (1000 * 60 * 60 * 24));

  return (
    <Card className="w-full max-w-md rounded-2xl shadow-lg overflow-hidden animate-in fade-in slide-in-from-bottom-4 duration-500">
      {/* Header with gradient */}
      <div className="relative bg-gradient-to-br from-primary/10 via-primary/5 to-transparent pt-8 pb-6 text-center">
        <div
          className="mx-auto w-20 h-20 rounded-2xl bg-card shadow-md flex items-center justify-center mb-5 animate-in zoom-in duration-300"
          style={{ animationDelay: '100ms' }}
        >
          <UserGroupIcon className="h-10 w-10 text-primary" />
        </div>
        <h1
          className="font-display text-3xl font-semibold text-foreground animate-in fade-in duration-300"
          style={{ animationDelay: '150ms' }}
        >
          <Trans>You&apos;re Invited!</Trans>
        </h1>
        <p
          className="text-muted-foreground mt-2 animate-in fade-in duration-300"
          style={{ animationDelay: '200ms' }}
        >
          <Trans>Join a household on TrackMyGroceries</Trans>
        </p>
      </div>

      <CardContent className="pt-6 pb-8 space-y-6">
        {/* Invitation Details */}
        <div
          className="rounded-xl bg-muted/30 border border-border/50 p-5 space-y-4 animate-in fade-in slide-in-from-bottom-2 duration-300"
          style={{ animationDelay: '250ms' }}
        >
          <div className="flex items-start gap-4">
            <div className="flex h-10 w-10 flex-shrink-0 items-center justify-center rounded-xl bg-primary/10">
              <HomeIcon className="h-5 w-5 text-primary" />
            </div>
            <div>
              <p className="text-xs text-muted-foreground uppercase tracking-wide">
                <Trans>Household</Trans>
              </p>
              <p className="font-display text-lg font-semibold text-foreground">
                {preview.household.name}
              </p>
            </div>
          </div>

          {preview.invitedByUser && (
            <div className="flex items-start gap-4">
              <div className="flex h-10 w-10 flex-shrink-0 items-center justify-center rounded-xl bg-muted">
                <EnvelopeIcon className="h-5 w-5 text-muted-foreground" />
              </div>
              <div>
                <p className="text-xs text-muted-foreground uppercase tracking-wide">
                  <Trans>Invited by</Trans>
                </p>
                <p className="font-medium text-foreground">
                  {preview.invitedByUser.name || preview.invitedByUser.email}
                </p>
              </div>
            </div>
          )}

          <div className="flex items-start gap-4">
            <div className="flex h-10 w-10 flex-shrink-0 items-center justify-center rounded-xl bg-fresh/10">
              <ShieldCheckIcon className="h-5 w-5 text-fresh" />
            </div>
            <div>
              <p className="text-xs text-muted-foreground uppercase tracking-wide">
                <Trans>Your Role</Trans>
              </p>
              <p className="font-medium text-foreground">{getRoleLabel(preview.invitation.role)}</p>
            </div>
          </div>
        </div>

        {/* Expiry Status */}
        <div
          className={cn(
            'flex items-center justify-center gap-2 py-2 px-4 rounded-lg text-sm',
            isExpired
              ? 'bg-destructive/10 text-destructive'
              : daysUntilExpiry <= 3
                ? 'bg-expiring-soon/10 text-expiring-soon'
                : 'bg-muted/50 text-muted-foreground'
          )}
          style={{ animationDelay: '300ms' }}
        >
          <ClockIcon className="h-4 w-4" />
          <span>
            {isExpired
              ? t`This invitation has expired`
              : daysUntilExpiry <= 1
                ? t`Expires today`
                : daysUntilExpiry <= 3
                  ? t`Expires in ${daysUntilExpiry} days`
                  : t`Expires ${expiryDate.toLocaleDateString(i18n.locale, {
                      month: 'short',
                      day: 'numeric',
                      year: 'numeric',
                    })}`}
          </span>
        </div>

        {/* Error Message */}
        {acceptError && (
          <div className="rounded-xl bg-destructive/10 border border-destructive/20 p-3 text-sm text-destructive text-center animate-in fade-in slide-in-from-top-2">
            {acceptError}
          </div>
        )}

        {/* Actions */}
        {!session ? (
          <div
            className="space-y-4 animate-in fade-in duration-300"
            style={{ animationDelay: '350ms' }}
          >
            <p className="text-sm text-center text-muted-foreground">
              <Trans>Sign in or create an account to accept this invitation</Trans>
            </p>
            <div className="flex gap-3">
              <Button asChild className="flex-1 h-11 rounded-xl" variant="outline">
                <Link to="/login" search={{ redirect: `/invitations/accept?token=${token}` }}>
                  <Trans>Sign In</Trans>
                </Link>
              </Button>
              <Button asChild className="flex-1 h-11 rounded-xl">
                <Link to="/signup" search={{ redirect: `/invitations/accept?token=${token}` }}>
                  <Trans>Create Account</Trans>
                </Link>
              </Button>
            </div>
          </div>
        ) : isExpired ? (
          <Button disabled className="w-full h-11 rounded-xl">
            <Trans>Invitation Expired</Trans>
          </Button>
        ) : (
          <div
            className="space-y-4 animate-in fade-in duration-300"
            style={{ animationDelay: '350ms' }}
          >
            <div className="flex items-center justify-center gap-2 text-sm text-muted-foreground">
              <div className="h-2 w-2 rounded-full bg-fresh" />
              <span>
                <Trans>
                  Signed in as <span className="font-medium text-foreground">{user?.email}</span>
                </Trans>
              </span>
            </div>
            <Button
              onClick={handleAccept}
              disabled={acceptInvitation.isPending}
              className="w-full h-12 rounded-xl text-base font-medium"
              size="lg"
            >
              {acceptInvitation.isPending ? (
                <div className="flex items-center gap-2">
                  <div className="h-4 w-4 animate-spin rounded-full border-2 border-white/30 border-t-white" />
                  <Trans>Joining...</Trans>
                </div>
              ) : (
                <>
                  <CheckCircleIcon className="h-5 w-5 mr-2" />
                  <Trans>Accept & Join Household</Trans>
                </>
              )}
            </Button>
          </div>
        )}
      </CardContent>
    </Card>
  );
}
